import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import React from "react";
import { ModalComponent } from "./Modal";

type ConfirmOptions = {
  title?: string;
  description?: string | React.ReactNode;
  confirmText?: string;
  cancelText?: string;
};

type ConfirmContextType = {
  confirm: (options: ConfirmOptions) => Promise<boolean>;
};

const ConfirmContext = React.createContext<ConfirmContextType | undefined>(
  undefined
);

export function useConfirm() {
  const ctx = React.useContext(ConfirmContext);
  if (!ctx) {
    throw new Error("useConfirm deve ser usado dentro de ConfirmProvider");
  }
  return ctx;
}

export function ConfirmProvider({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = React.useState(false);
  const [resolver, setResolver] = React.useState<
    ((value: boolean) => void) | null
  >(null);
  const [opts, setOpts] = React.useState<ConfirmOptions>({});

  const confirm = (options: ConfirmOptions) => {
    setOpts(options);
    setOpen(true);
    return new Promise<boolean>((resolve) => setResolver(() => resolve));
  };

  const handleClose = () => {
    setOpen(false);
    if (resolver) resolver(false);
    setResolver(null);
  };

  const handleConfirm = () => {
    setOpen(false);
    if (resolver) resolver(true);
    setResolver(null);
  };

  return (
    <ConfirmContext.Provider value={{ confirm }}>
      {children}
      <ModalComponent
        open={open}
        handleClose={handleClose}
        autoHeight
        title={opts.title || "Confirmar ação"}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {opts.description &&
            (typeof opts.description === "string" ? (
              <Typography>{opts.description}</Typography>
            ) : (
              opts.description
            ))}
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5 }}>
            <Button variant="outlined" onClick={handleClose}>
              {opts.cancelText || "Cancelar"}
            </Button>
            <Button variant="contained" onClick={handleConfirm}>
              {opts.confirmText || "Confirmar"}
            </Button>
          </Box>
        </Box>
      </ModalComponent>
    </ConfirmContext.Provider>
  );
}
